<template>
  <panel :title="schoolModel.$title">
    <div v-if="schoolSelected" slot="toolbar" class="text-right">
      <button class="btn btn-light mr-2" @click="clearSchema">
        <span>Zmień</span>
      </button>
    </div>

    <div v-if="!schoolModel.manualInput.$model && !schoolSelected">
      <p>
        Wybierz szkołę z list poniżej, a w przypadku jej braku kliknij na przycisk "Dodaj szkołę ręcznie", który
        wyświetli dodatkowe pola umożliwiające wprowadzenie danych szkoły samodzielnie.
      </p>

      <w-radio-list
        class="mb-3"
        :model="$schema.public"
        :values="[
          { text: 'Szkoły prowadzone przez Miasto Lublin', value: true },
          { text: 'Szkoły prowadzone przez inne podmioty', value: false }
        ]"
      />

      <school-picker
        v-if="$schema.public.$model !== null"
        class="form-group"
        :public="$schema.public.$model"
        :school-model="schoolModel"
      />

      <button v-if="$schema.public.$model === false" class="btn btn-light mt-2" @click="openManualForm">
        <span>Dodaj szkołę ręcznie</span>
      </button>
    </div>

    <div v-if="schoolModel.manualInput.$model">
      <w-input class="form-group" :model="schoolModel.manualSchoolName" @input="getSchoolByNumberExactName" />

      <w-input class="form-group" :model="schoolModel.manualSubSchoolName" @input="getSchoolByNumberExactName" />

      <div v-if="foundSchool" class="form-group">
        <a class="pointer" @click.prevent="saveFoundSchool">
          Czy chodziło o
          <b>
            <i>{{ foundSchool.name }} </i>
          </b>
        </a>
      </div>

      <w-radio-list class="form-group" :model="schoolModel.reportType" :values="schoolTypes" />

      <address-row :model="schoolModel.schoolAddress" />
    </div>

    <div v-if="schoolModel.manualInput.$model" slot="footer" class="text-right">
      <button class="btn btn-light mr-2" @click="cancel">
        <span>Anuluj</span>
      </button>

      <button class="btn btn-light" @click="save">
        <span>Zapisz</span>
      </button>
    </div>

    <div v-if="schoolSelected">
      <p v-if="schoolModel.schoolName.$model">
        {{ schoolModel.schoolName.$title }}:
        <b>{{ schoolModel.schoolName.$model }}</b>
      </p>

      <p v-if="schoolModel.reportType.$model">
        {{ schoolModel.reportType.$title }}:
        <b>{{ schoolModel.reportType.$model }}</b>
      </p>

      <p v-if="schoolModel.subSchoolName.$model">
        {{ schoolModel.subSchoolName.$title }}:
        <b>{{ schoolModel.subSchoolName.$model }}</b>
      </p>

      <p v-if="schoolModel.schoolAddress.$model">
        {{ schoolModel.schoolAddress.$title }}:
        <b>{{ schoolModel.schoolAddress.$model | formatAddress }}</b>
      </p>
    </div>
  </panel>
</template>

<script>
import SchoolPicker from '@/components/forms/document/SchoolPicker';
import Panel from '@/components/panel/Panel.vue';
import { Errors } from '@/enums/errors.enum';
import { formatAddress } from '@/filters/formatAddress';
import { ow003Mixin } from '@/mixins/document/ow003';
import { setSchoolDataMixin } from '@/mixins/document/setSchoolData';
import { formMixin } from '@/mixins/form';
import { notificationsMixin } from '@/mixins/notifications';
import schema from '@/schemas/schoolManual.schema';

import AddressRow from './Address.vue';

// @vuese
// Formularz do wyboru szkoły
export default {
  components: {
    Panel,
    AddressRow,
    SchoolPicker
  },
  filters: { formatAddress },
  mixins: [formMixin, notificationsMixin, ow003Mixin, setSchoolDataMixin],
  schema,
  props: {
    // schema szkoły
    schoolModel: { type: Object, required: true }
  },

  data() {
    return {
      isSubmittedManually: false,
      model: {},
      foundSchool: null
    };
  },

  computed: {
    schoolSelected() {
      const model = this.schoolModel.$model;
      return (model.nodeId && (!model.isGroup || model.subSchoolId)) || this.isSubmittedManually;
    },
    hasWarnings() {
      return this.schoolModel.$checkAll().some(item => item.$warnings.length);
    }
  },
  watch: {
    'schoolModel.schoolType.$warnings': function(newVal) {
      if (newVal?.length) {
        this.$schema.$validateAll();
      }
    }
  },

  mounted() {
    this.checkIsSubmittedManually();
  },

  methods: {
    // @vuese
    // Zapisuje szkołe dodaną ręcznie
    save() {
      this.getSchoolByExactName().then(school => {
        if (school) {
          this.foundSchool = school;
          this.saveFoundSchool();
        } else if (!this.hasWarnings) {
          this.schoolModel.manualInput.$setData(false);
          this.isSubmittedManually = true;
        }
      });
    },
    // @vuese
    // Sprawdza czy szkoła została dodana ręcznie
    checkIsSubmittedManually() {
      this.isSubmittedManually = !this.hasWarnings;
      this.schoolModel.$reset();
    },
    // @vuese
    // Czyści dane schemy
    clearSchema() {
      this.$schema.$setDefault();
      this.$schema.$reset();
      this.schoolModel.$setDefault();
      this.schoolModel.$reset();
      this.isSubmittedManually = false;
      this.$emit('clear');
    },

    openManualForm() {
      this.clearSchema();
      this.schoolModel.manualInput.$setData(true);
    },

    // @vuese
    // Anuluje ręczne dodawanie szkoły
    cancel() {
      this.clearSchema();
    },

    // @vuese
    // Szuka szkoły na podstawie wpisanych danych
    getSchoolByExactName() {
      const names = [this.schoolModel.manualSubSchoolName.$model, this.schoolModel.manualSchoolName.$model].filter(
        Boolean
      );
      if (!names.length) {
        return Promise.resolve(undefined);
      }

      const firstSchoolName = names.shift();
      return this.querySchoolByExactName(firstSchoolName).then(school => {
        if (!school && names.length) {
          return this.querySchoolByExactName(names[0]);
        }
        return school;
      });
    },

    // @vuese
    // Szuka szkoły po dokładnej nazwie
    querySchoolByExactName(searchExact) {
      return this.$store
        .dispatch('structureNode/list', {
          searchExact,
          hasParent: true,
          preventLoader: true
        })
        .then(response => response?.list?.[0]);
    },

    // @vuese
    // Szuka szkoły po nazwie z precyzyjną liczbą
    getSchoolByNumberExactName(search) {
      this.$store
        .dispatch('structureNode/list', {
          searchExactNumber: search,
          hasParent: true,
          preventLoader: true
        })
        .then(results => (this.foundSchool = results?.list?.[0]))
        .catch(err => {
          this.foundSchool = undefined;
          this.notifyError(Errors.STRUCTURE_CANNOT_LIST);
        });
    },

    // @vuese
    // Zapisuje znalezioną szkołę jako wybór
    saveFoundSchool() {
      this.updateSchoolData(this.foundSchool);
      this.foundSchool = undefined;
      this.schoolModel.manualInput.$setData(false);
    }
  }
};
</script>
